import {useMachine} from '@xstate/react';

import {useCallback} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {machine} from '../machine/machine';
import {BaseVehicle} from '../types/BaseVehicle';

/**
 * Hook to connect React components to the vehicle form XState machine
 * Provides centralized, visible side effects for field dependencies
 */
export function useVehicleFlow() {
  const [state, send, machineActor] = useMachine(machine);

  // Selectors
  const errors = state.context.errors;

  // Derived state
  const hasErrors = errors.length > 0;

  const initMachine = useCallback(
    (
      form: UseFormReturn<BaseVehicle>,
      getCatalogMakesForType?: (vehicleType: string) => Promise<string[]>,
      getCatalogModelsForTypeAndMake?: (vehicleType: string, make: string) => Promise<string[]>
    ) => {
      send({type: 'START_WATCHING', form, getCatalogMakesForType, getCatalogModelsForTypeAndMake});
    },
    [send]
  );

  const stopMachine = useCallback(() => {
    // Send unconditionally — the machine only handles STOP_WATCHING in 'watchingActive'
    // (its normal steady state). Sending it from any other state is a safe no-op because
    // XState ignores events that have no handler in the current state.
    send({type: 'STOP_WATCHING'});
  }, [send]);

  return {
    initMachine,
    stopMachine,
    // State
    errors,

    // Derived state
    hasErrors,

    // Raw machine interface for advanced usage
    state,
    send,
    machineActor,
  };
}
